'use client';

import classNames from 'classnames';
import type { PaddingOptions } from 'mapbox-gl';
import dynamic from 'next/dynamic';
import { usePathname, useSearchParams } from 'next/navigation';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import useAdvancedMapLayersPeakValidation from '@alltrails/advanced-map-layers/hooks/useAdvancedMapLayersPeakValidation';
import useFallFoliageTierValidation from '@alltrails/advanced-map-layers/hooks/useFallFoliageTierValidation';
import logExploreMapDistanceAwayDropdownClicked from '@alltrails/amplitude/events/logExploreMapDistanceAwayDropdownClicked';
import logMapViewAdjustmentClicked from '@alltrails/amplitude/events/logMapViewAdjustmentClicked';
import useConditionsValidation from '@alltrails/conditions/hooks/useConditionsValidation';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import { CONDITIONS_FEATURE_ENTITLEMENTS } from '@alltrails/context/hooks/useFeatureEntitlements';
import useUser from '@alltrails/context/hooks/useUser';
import Toast from '@alltrails/denali/components/Toast';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import ExploreFiltersBar from '@alltrails/explore-service/components/ExploreFiltersBar/ExploreFiltersBar';
import { updateBoundingBox } from '@alltrails/explore-service/redux/exploreServiceSlice';
import getMapBoundingBox from '@alltrails/explore-service/utils/getMapBoundingBox';
import useHeatmapsTierValidation from '@alltrails/heatmaps/hooks/useHeatmapsTierValidation';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import type ListItem from '@alltrails/lists/types/ListItem';
import BaseMap from '@alltrails/maps/components/BaseMap/BaseMap';
import ClickableIcons from '@alltrails/maps/components/ClickableIcons/ClickableIcons';
import DistanceAwayCenterMarker from '@alltrails/maps/components/DistanceAwayCenterMarker/DistanceAwayCenterMarker';
import { dynamicStyleImages } from '@alltrails/maps/components/DynamicStyleOverlays/index';
import Isochrone from '@alltrails/maps/components/Isochrone/Isochrone';
import MapAttribution from '@alltrails/maps/components/MapAttribution/MapAttribution';
import MapCoordinates from '@alltrails/maps/components/MapCoordinates/MapCoordinates';
import MapLayout from '@alltrails/maps/components/MapLayout/MapLayout';
import {
  DEFAULT_HEATMAP_STYLE,
  type HeatmapLayerStyle,
  SATELLITE_HEATMAP_STYLE
} from '@alltrails/maps/components/PolylineHeatmapLayer/PolylineHeatmapLayer';
import useCurrentLocationButton from '@alltrails/maps/hooks/useCurrentLocationButton';
import useMap from '@alltrails/maps/hooks/useMap';
import { setHideTrailPins, updateMapZoomBucket, updateNearbyTrailsPinTreatment } from '@alltrails/maps/redux/reducer';
import type { BaseStyleId, DynamicStyleId } from '@alltrails/maps/types/Styles';
import { defaultSupportedDynamicStyleIds } from '@alltrails/maps/utils/constants';
import getZoomBucket from '@alltrails/maps/utils/getZoomBucket';
import type { FeatureEntitlement } from '@alltrails/types/FeatureEntitlement';
import useCurrentListItemActions from '@/hooks/lists/useCurrentListItemActions';
import useExploreConditions from '@/hooks/useExploreConditions';
import useIsPage from '@/hooks/useIsPage';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { setHasNavigatedClientSide, setIsShowingMobileList, setListToast, setMapPadding } from '@/utils/slices/exploreSlice';
import BuildCustomRouteButton from '../BuildCustomRouteButton/BuildCustomRouteButton';
import ExploreConditionsMobileWarning from '../ExploreConditionsMobileWarning/ExploreConditionsMobileWarning';
import MapCenterMarker from '../MapCenterMarker/MapCenterMarker';
import MobileViewToggle from '../MobileViewToggle/MobileViewToggle';
import StreetAddressMapMarker from '../StreetAddressMapMarker/StreetAddressMapMarker';
import styles from './ExploreMap.module.scss';
import { type ExploreMapControlsContextValue, ExploreMapControlsProvider } from './ExploreMapControlsContext';
import ExploreMapOverlays from './ExploreMapOverlays';
import HeatmapPocControls from './HeatmapPocControls';
import MapControls from './MapControls';
import MapLegends from './MapLegends';
import useHeatmapPocControlsToggle from './useHeatmapPocControlsToggle';

const AdvancedMapsUpsellModal = dynamic(() => import('@alltrails/advanced-map-layers/components/AdvancedMapsUpsellModal/AdvancedMapsUpsellModal'), {
  ssr: false
});
const PublicLandsLegend = dynamic(() => import('@alltrails/advanced-map-layers/components/PublicLandsLegend/PublicLandsLegend'), { ssr: false });
const ConditionsUpsellModal = dynamic(() => import('@alltrails/conditions/components/ConditionsUpsellModal'), { ssr: false });
const HeatmapErrorToast = dynamic(() => import('@alltrails/heatmaps/components/HeatmapErrorToast'), { ssr: false });
const HeatmapFilterModal = dynamic(() => import('@alltrails/heatmaps/components/HeatmapFilterModal'), { ssr: false });
const HeatmapsUpsellModal = dynamic(() => import('@alltrails/heatmaps/components/HeatmapsUpsellModal'), { ssr: false });
const FallFoliageUpsellModal = dynamic(() => import('@alltrails/advanced-map-layers/components/FallFoliageUpsellModal/FallFoliageUpsellModal'), {
  ssr: false
});
const images = dynamicStyleImages;

const onCurrentLocationButtonClick = () => logMapViewAdjustmentClicked({ action_taken: 'view current location clicked' });

type ExploreMapProps = {
  bottomCenterContent?: ReactNode;
  children?: ReactNode;
  mapComponents?: ReactNode;
  mobileLocationCTA?: ReactNode;
  printControl?: ReactNode;
  sidebar?: ReactNode;
  topLeftContent?: ReactNode;
};

const ExploreMap = ({ bottomCenterContent, children, mapComponents, mobileLocationCTA, printControl, sidebar, topLeftContent }: ExploreMapProps) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const pathname = usePathname();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    dispatch(setHasNavigatedClientSide(true));
  }, [dispatch, pathname]);

  const searchParams = useSearchParams();
  const showNewMapLayersCallout = searchParams.get('map-layers-gtm') === 'true';

  const user = useUser();
  const displayMetric = useAppSelector(state => state.context.displayMetric);
  const experiments = useExperiments();
  const debugEnabled = experiments?.['mugen-explore-debug']?.value === 'on';
  const isPhotoHeatmapEnabled = experiments?.['web-tmt-rm-photos-overlay']?.value === 'on';
  const isRecordingOverlaysEnabled = experiments?.['web-tmt-rm-recording-overlays']?.value === 'on';
  const isFallFoliageEnabled = experiments?.['web-disco-rm-fall-foliage']?.value === 'on';
  const { hasPermission } = useAuthorization();
  const hasTrailsManagePermission = hasPermission({ permission: 'trails:manage' });
  const {
    isCustomRoute,
    isNewCustomRoute,
    isTrailCustomRoute,
    isExplorePOI,
    isExplorePark,
    isExploreRecentlyViewed,
    isExploreRoot,
    isMembersCompleted,
    isMembersCustomRoute,
    isExploreTrail,
    isExploreRecording,
    isTripPlanner
  } = useIsPage();
  const isMobile = useIsMobileSizedScreen();
  const { isFavorite, handleFavoriteClick, listModal, signUpModal: listSignUpModal } = useReduxListItems();
  const { listPageInfo, listToast, isRouteBuildingEnabled } = useAppSelector(state => ({
    listPageInfo: state.explore.listPageInfo,
    listToast: state.explore.listToast,
    isRouteBuildingEnabled: state.customRoute.isRouteBuildingEnabled
  }));

  const { addToCurrentList, isInCurrentList, removeFromCurrentList, undoAdd, undoRemove } = useCurrentListItemActions(
    listPageInfo?.listId ?? 0,
    listPageInfo?.isOwner ?? false
  );
  const listItemsProps = useMemo(
    () => ({
      isFavorite: (itemType: ListItem['type'], itemId: number) => {
        if (listPageInfo && itemType === 'trail' && listPageInfo.isEditable) {
          return isInCurrentList('trail', itemId);
        }
        return isFavorite(itemType, itemId);
      },
      onFavoriteClick: (itemType: ListItem['type'], itemId: number) => {
        if (listPageInfo && itemType === 'trail' && listPageInfo.isEditable) {
          if (isInCurrentList('trail', itemId)) {
            removeFromCurrentList('trail', itemId);
          } else {
            addToCurrentList('trail', itemId);
          }
          return;
        }
        handleFavoriteClick(itemType, itemId);
      },
      listModal,
      signUpModal: listSignUpModal
    }),
    [isFavorite, handleFavoriteClick, listModal, listSignUpModal, listPageInfo, addToCurrentList, isInCurrentList, removeFromCurrentList]
  );

  const {
    showFilterBar: showFilterBarForRoute,
    initialMapBounds,
    initialMapCenter,
    isMobileListAtTop,
    headerHeight,
    panelCollapsed,
    distanceAway,
    distanceAwayIsochrone,
    dynamicStyleIds,
    isHeatmapFilterModalOpen,
    isEditingCustomRoute
  } = useAppSelector(state => ({
    showFilterBar: state.explore.showFilterBar,
    initialMapBounds: state.explore.initialMapBounds,
    initialMapCenter: state.explore.initialMapCenter,
    isMobileListAtTop: state.explore.isMobileListAtTop,
    headerHeight: state.explore.headerHeight,
    panelCollapsed: state.explore.panelCollapsed,
    distanceAway: state.exploreService.distanceAway,
    distanceAwayIsochrone: state.exploreService.distanceAwayIsochrone,
    dynamicStyleIds: state.map.dynamicStyleIds,
    isHeatmapFilterModalOpen: state.heatmaps.isHeatmapFilterModalOpen,
    isEditingCustomRoute: !!state.customRoute.isEditing
  }));
  const showFilterBar = showFilterBarForRoute && !isTripPlanner;
  const disablePopups = isEditingCustomRoute && isRouteBuildingEnabled;

  const { showHeatmapsUpsellModal, onHeatmapsUpsellModalCloseRequest, handleHeatmapFailedEntitlementCheck } = useHeatmapsTierValidation();
  const { showConditionsUpsellModal, onConditionsUpsellModalCloseRequest, handleConditionsFailedEntitlementCheck } = useConditionsValidation();
  const { showFoliageUpsellModal, onFoliageUpsellModalCloseRequest, handleFoliageFailedEntitlementCheck } = useFallFoliageTierValidation();

  const { advancedMapsUpsellTrigger, handleAdvancedMapLayersEntitlementCheck, onUpsellModalCloseRequest, showAdvancedMapsUpsellModal } =
    useAdvancedMapLayersPeakValidation();

  const pageSupportsConditionOverlays =
    !isMobile &&
    (isExplorePOI || isExploreRecentlyViewed || isExploreRoot || isExplorePark || isExploreTrail || isCustomRoute || isMembersCustomRoute);
  useExploreConditions();

  const publicLandsIsApplied = useMemo(() => dynamicStyleIds.includes('publicLands'), [dynamicStyleIds]);
  const pageSupportsCommunityActivity = !isMembersCustomRoute;
  const supportedDynamicStyleIds = useMemo(() => {
    const ids: DynamicStyleId[] = [...defaultSupportedDynamicStyleIds, 'wildfire'];

    if (!hasTrailsManagePermission && pageSupportsCommunityActivity) {
      ids.push('communityActivity');
    }

    // Show photospots for admins on /explore
    if (hasTrailsManagePermission && isExploreRoot) {
      ids.push('photospot');
    }

    if (isMembersCompleted) {
      ids.push('nearbyTrails');
    }

    if (isRecordingOverlaysEnabled) {
      ids.push('recordingStart', 'recordingEnd');
    }
    if (isPhotoHeatmapEnabled) {
      ids.push('photoHeatmap');
    }

    if (isFallFoliageEnabled) {
      ids.push('fallFoliage');
    }

    if (pageSupportsConditionOverlays) {
      ids.push('precipitation', 'temperature', 'snowDepth');
    }

    // Ground conditions colours the route by surface type, so it needs a real trail with surface segments —
    // unlike the other conditions, which work anywhere. surfaceType is applied alongside it by conditionsMiddleware.
    if (pageSupportsConditionOverlays && isExploreTrail) {
      ids.push('groundConditions', 'surfaceType');
    }

    ids.push('userWaypoints');

    if (isCustomRoute || isMembersCustomRoute || isExploreRecording) {
      ids.push('distanceMarkers');
    }

    if (
      (isCustomRoute || isTrailCustomRoute) &&
      !isNewCustomRoute &&
      isEditingCustomRoute &&
      user?.featureEntitlements?.includes('build_your_own_trail') // peak-only feature
    ) {
      // only display this layer if there is a previously saved route or original custom route/trail being edited
      ids.push('savedRoutePolyline');
    }

    ids.push('communityWaypoints');

    return ids;
  }, [
    hasTrailsManagePermission,
    isExploreRoot,
    isMembersCompleted,
    pageSupportsConditionOverlays,
    pageSupportsCommunityActivity,
    isExploreTrail,
    isCustomRoute,
    isNewCustomRoute,
    isTrailCustomRoute,
    isMembersCustomRoute,
    isExploreRecording,
    isEditingCustomRoute,
    isPhotoHeatmapEnabled,
    isRecordingOverlaysEnabled,
    isFallFoliageEnabled,
    user?.featureEntitlements
  ]);

  useEffect(() => {
    if (!isMembersCompleted) {
      return undefined;
    }
    dispatch(updateNearbyTrailsPinTreatment('trail'));
    return () => {
      dispatch(updateNearbyTrailsPinTreatment('admin'));
    };
  }, [dispatch, isMembersCompleted]);

  const handleFailedEntitlementCheck = useCallback(
    (entitlement: FeatureEntitlement, applyStyle: () => void, styleId?: DynamicStyleId | BaseStyleId) => {
      if (CONDITIONS_FEATURE_ENTITLEMENTS.includes(entitlement)) {
        handleConditionsFailedEntitlementCheck(applyStyle);
      } else if (entitlement === 'community_heat_maps') {
        handleHeatmapFailedEntitlementCheck(applyStyle);
      } else if (entitlement === 'advanced_map_layers' && styleId) {
        handleAdvancedMapLayersEntitlementCheck(styleId, applyStyle);
      } else if (entitlement === 'fall_foliage') {
        handleFoliageFailedEntitlementCheck(applyStyle);
      }
    },
    [
      handleConditionsFailedEntitlementCheck,
      handleHeatmapFailedEntitlementCheck,
      handleAdvancedMapLayersEntitlementCheck,
      handleFoliageFailedEntitlementCheck
    ]
  );

  const isLayerUpsellOpen = showHeatmapsUpsellModal || showConditionsUpsellModal || showAdvancedMapsUpsellModal;

  const handleMapMoveEnd = useCallback(
    (zoom: number) => {
      const zoomBucket = getZoomBucket(zoom);
      dispatch(updateMapZoomBucket(zoomBucket));
    },
    [dispatch]
  );

  const map = useMap();

  const onMapPaddingChange = useCallback(
    (newPadding: PaddingOptions) => {
      dispatch(setMapPadding(newPadding));
      if (map) {
        dispatch(updateBoundingBox(getMapBoundingBox(map)));
      }
    },
    [dispatch, map]
  );

  const { hideTrailPins, baseStyleId, isPersonalHeatmapActive } = useAppSelector(state => ({
    hideTrailPins: state.map.hideTrailPins ?? false,
    baseStyleId: state.map.baseStyleId,
    isPersonalHeatmapActive: state.map.dynamicStyleIds.includes('personalHeatmap')
  }));
  const handleHideTrailPinsChange = useCallback((hide: boolean) => dispatch(setHideTrailPins(hide)), [dispatch]);
  const isSatelliteBasemap = baseStyleId === 'alltrailsSatelliteV2' || baseStyleId === 'googleSatellite';
  // Admin POC override; undefined means "use the basemap-aware default in PersonalHeatmap"
  const [heatmapStyle, setHeatmapStyle] = useState<HeatmapLayerStyle | undefined>(undefined);
  const isHeatmapPocControlsToggledOn = useHeatmapPocControlsToggle();

  // What a control rendered in a slot needs from the map it sits on. The overlays are a second
  // instance, not the live map's — a control that prints renders a map of its own.
  const controlsContextValue = useMemo<ExploreMapControlsContextValue>(
    () => ({
      handleFailedEntitlementCheck,
      isLayerUpsellOpen,
      mapOverlays: <ExploreMapOverlays disablePopups heatmapStyle={heatmapStyle} showConditionOverlays={pageSupportsConditionOverlays} />
    }),
    [handleFailedEntitlementCheck, heatmapStyle, isLayerUpsellOpen, pageSupportsConditionOverlays]
  );

  const [showDistanceAwayMarker, setShowDistanceAwayMarker] = useState(false);
  const onDistanceAwayFilterClose = useCallback(() => {
    setShowDistanceAwayMarker(false);
  }, []);
  const onDistanceAwayFilterOpen = useCallback(() => {
    logExploreMapDistanceAwayDropdownClicked();
    setShowDistanceAwayMarker(true);
    // Kick users to the map view since the DA filter is much more while viewing the map
    dispatch(setIsShowingMobileList(false));
  }, [dispatch]);

  const renderMapOverlayChildren = useCallback(
    (mapPadding: PaddingOptions) => {
      // Only show the marker when a distance away filter isn't already applied.
      // When one is already applied, we don't reset the center point, so don't need the marker visible.

      return !distanceAway && showDistanceAwayMarker ? (
        <div
          className={styles.mapArea}
          style={{
            top: mapPadding.top ?? 0,
            left: mapPadding.left ?? 0,
            bottom: mapPadding.bottom ?? 0,
            right: mapPadding.right ?? 0
          }}
        >
          <MapCenterMarker className={styles.mapCenterMarker} />
        </div>
      ) : null;
    },
    [distanceAway, showDistanceAwayMarker]
  );

  const { currentLocationMarker, currentLocationButton, currentLocationModal } = useCurrentLocationButton(onCurrentLocationButtonClick);

  // The sidebar slot actually renders the mobile results list on mobile
  const desktopSidebar = isMobile ? undefined : sidebar;
  const mobileResultsList = isMobile ? sidebar : undefined;

  const mapLayoutTopLeftContent =
    showFilterBar && !isMobile ? (
      <ExploreFiltersBar
        filtersContainerClassName={styles.desktopFiltersContainer}
        map={map}
        onDistanceAwayFilterClose={onDistanceAwayFilterClose}
        onDistanceAwayFilterOpen={onDistanceAwayFilterOpen}
      />
    ) : (
      topLeftContent
    );

  // On mobile only the route builders (new / trail-based) are roadblocked; saved-route and members views render normally.
  if (isMobile && (isNewCustomRoute || isTrailCustomRoute)) {
    return null;
  }

  const canSeeMapCoordinates = user?.featureEntitlements?.includes('build_your_own_trail') && isEditingCustomRoute;

  return (
    <ExploreMapControlsProvider value={controlsContextValue}>
      <div
        className={styles.container}
        // Note that changes to this style seem trigger an async map resize operation
        // This means that effects listening to changes to headerHeight from the redux store may be triggered before the map has been resized
        style={!!headerHeight ? { height: `calc(100dvh - ${headerHeight}px)` } : {}}
      >
        {isMobile && showFilterBar && (
          <div className={classNames(styles.mobileFilterBar, { [styles.shadow]: !isMobileListAtTop })}>
            <ExploreFiltersBar
              filtersContainerClassName={styles.mobileFiltersContainer}
              map={map}
              onDistanceAwayFilterClose={onDistanceAwayFilterClose}
              onDistanceAwayFilterOpen={onDistanceAwayFilterOpen}
            />
          </div>
        )}
        {(initialMapBounds || initialMapCenter) && (
          <div className={styles.mapContainer}>
            <BaseMap
              disableDefaultAttribution
              initialBounds={initialMapBounds}
              initialCenter={initialMapCenter}
              images={images}
              listItemsProps={listItemsProps}
              preventReuseMaps
              supportedDynamicStyleIds={supportedDynamicStyleIds}
              onMoveEnd={({ zoom }) => handleMapMoveEnd(zoom)}
            >
              {distanceAwayIsochrone ? <Isochrone polygon={distanceAwayIsochrone} /> : null}
              {distanceAwayIsochrone && distanceAway ? (
                <DistanceAwayCenterMarker latitude={distanceAway.center.lat} longitude={distanceAway.center.lng} />
              ) : null}
              <StreetAddressMapMarker />
              <ExploreMapOverlays
                currentLocationMarker={currentLocationMarker}
                disablePopups={disablePopups}
                heatmapStyle={heatmapStyle}
                showConditionOverlays={pageSupportsConditionOverlays}
              />

              <HeatmapErrorToast />
              <MapAttribution />
              {mapComponents}
              <ClickableIcons />
            </BaseMap>
            {/* Admin-only styling surface for the personal polyline heatmap.
                Renders only while the heatmap is active so admins can iterate
                on the live layer; not part of the production design system.
                Toggle visibility with Option+Shift+H. */}
            {hasTrailsManagePermission && isPersonalHeatmapActive && isHeatmapPocControlsToggledOn && (
              <HeatmapPocControls
                hideTrailPins={hideTrailPins}
                onHideTrailPinsChange={handleHideTrailPinsChange}
                style={heatmapStyle ?? (isSatelliteBasemap ? SATELLITE_HEATMAP_STYLE : DEFAULT_HEATMAP_STYLE)}
                onStyleChange={setHeatmapStyle}
              />
            )}
            <MapLayout
              bottomCenterContent={bottomCenterContent}
              legends={
                <>
                  {isMobile && (
                    <div className={styles.mobileCTAs}>
                      <MobileViewToggle />
                      {mobileLocationCTA}
                    </div>
                  )}
                  <MapLegends mapLocation={isCustomRoute || isMembersCustomRoute ? 'custom route' : 'explore'} />
                </>
              }
              topRightControls={
                <MapControls
                  currentLocationButton={currentLocationButton}
                  handleFailedEntitlementCheck={handleFailedEntitlementCheck}
                  pauseFocusTrap={isLayerUpsellOpen}
                  showNewMapLayersCallout={showNewMapLayersCallout}
                />
              }
              onMapPaddingChange={onMapPaddingChange}
              bottomRightControls={
                <>
                  {isExploreRoot ? <BuildCustomRouteButton /> : null}
                  {printControl}
                  {debugEnabled && map && (
                    <div className={styles.debugInfo}>
                      <span>Zoom: {Math.round(map.getZoom() * 10000) / 10000}</span>
                    </div>
                  )}
                  {!isMobile && <PublicLandsLegend isOpen={publicLandsIsApplied} />}
                </>
              }
              renderChildren={renderMapOverlayChildren}
              sidebar={desktopSidebar}
              sidebarCollapsed={panelCollapsed}
              topLeftContent={mapLayoutTopLeftContent}
              topRightContent={canSeeMapCoordinates ? <MapCoordinates className={styles.mapCoordinates} displayMetric={displayMetric} /> : null}
            />
            {isMobile && (
              <div className={classNames(styles.mobileMapOverlays, { [styles.isShowingMobileList]: Boolean(mobileResultsList) })}>
                {mobileResultsList}
              </div>
            )}
          </div>
        )}
        {children}
      </div>
      {currentLocationModal}
      {isHeatmapFilterModalOpen && <HeatmapFilterModal />}
      <HeatmapsUpsellModal
        isOpen={showHeatmapsUpsellModal}
        onCloseRequest={onHeatmapsUpsellModalCloseRequest}
        upsellTrigger="community heatmaps layer tapped explore map"
      />
      <ConditionsUpsellModal
        isOpen={showConditionsUpsellModal}
        onCloseRequest={onConditionsUpsellModalCloseRequest}
        upsellTrigger="advanced conditions layer tapped"
      />
      <AdvancedMapsUpsellModal
        isOpen={showAdvancedMapsUpsellModal}
        onCloseRequest={onUpsellModalCloseRequest}
        upsellTrigger={advancedMapsUpsellTrigger}
      />
      <FallFoliageUpsellModal isOpen={showFoliageUpsellModal} onCloseRequest={onFoliageUpsellModalCloseRequest} />
      <ExploreConditionsMobileWarning isMobile={isMobile} />
      {listPageInfo && (
        <>
          <Toast
            isOpen={listToast?.type === 'added'}
            setIsOpen={open => {
              if (!open) {
                dispatch(setListToast(null));
              }
            }}
            type="neutral"
            position="alternative"
            timeout={3000}
            message={intl.formatMessage({ defaultMessage: 'Saved to {listName}' }, { listName: listPageInfo.listName })}
            action={undoAdd}
            actionText={intl.formatMessage({ defaultMessage: 'Undo' })}
          />
          <Toast
            isOpen={listToast?.type === 'removed'}
            setIsOpen={open => {
              if (!open) {
                dispatch(setListToast(null));
              }
            }}
            type="neutral"
            position="alternative"
            timeout={3000}
            message={intl.formatMessage({ defaultMessage: 'Removed from {listName}' }, { listName: listPageInfo.listName })}
            action={undoRemove}
            actionText={intl.formatMessage({ defaultMessage: 'Undo' })}
          />
        </>
      )}
    </ExploreMapControlsProvider>
  );
};

export default ExploreMap;
